import { COLORS } from '@consts/general';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { basicSort, dateSort } from '@utils/sort';
import { average, countUnique } from '@utils/summaries';
import { useRange } from '@hooks/useRange';
import { getUniqueDates, groupBy } from '@utils/data';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';
import { REPORT } from '@consts/reports';

interface WarrantyRateByMonthProps {
    dateRange: { startDate: string; endDate: string };
}

export const WarrantyRateByMonth = ({ dateRange }: WarrantyRateByMonthProps) => {
    const [range, setRange] = useRange('WarrantyRateByMonth');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.WARRANTY_RATE_BY_MONTH_COMPARED_TO_GLOBAL_AVERAGE,
        params: { range, ...dateRange },
    });

    const dateKeys = getUniqueDates(data, range);

    const groupedByDate = groupBy(data, 'date');

    const globalAvgSeries = {
        data: dateKeys.map(({ date }) => groupedByDate[date]?.[0]?.globalWarrantyAvg),
        type: 'column',
        name: 'Global Avg',
        color: COLORS.BLUE,
        yAxis: 0,
        dataLabels: {
            enabled: true,
            color: 'black',
        },
    };

    const companyWarrSeries = {
        data: dateKeys.map(({ date }) => groupedByDate[date]?.[0]?.companyWarrantyAvg),
        type: 'line',
        name: 'Warranty %',
        color: COLORS.YELLOW_DARK,
        yAxis: 1,
        dataLabels: {
            enabled: true,
        },
        marker: {
            fillColor: COLORS.RED,
            radius: 3,
        },
    };

    return (
        <BaseReport
            setRange={setRange}
            title={'Warranty Rate by Month'}
            description={
                'This graph shows your overall warranty rate (warranty cuts divided by total cuts) by month, comparing it to the global average, for the date range selected above.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            range={range}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: ['Date', 'Global Avg', 'Warranty %'],
                data: data.map((item) => [item.date, item.globalWarrantyAvg, item.companyWarrantyAvg]),
                summaries: [countUnique, average, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort],
            }}
            chartOptions={chart(
                300,
                [
                    { name: 'Warranty Percentage', opposite: false },
                    { name: 'Global Average', opposite: true },
                ],
                dateKeys,
                [globalAvgSeries, companyWarrSeries],
                { min: 0 },
                {
                    legend: {
                        align: 'right',
                        verticalAlign: 'top',
                    },
                }
            )}
        />
    );
};
